import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Button,
  Card,
  Collapse,
  Descriptions,
  Form,
  Grid,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  Timeline,
  Upload,
} from "antd";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import * as dayjs from "dayjs";
import { useFirebase, useFirestore } from "react-redux-firebase";
import { FIRESTORE_PRODUCTS_TABLE, FIRESTORE_PURCHASE_ORDERS_TABLE } from "../../../../constants/FirestoreConstant";
import {
  PURCHASE_ORDER_STATUS_COMPLETED,
  PURCHASE_ORDER_STATUS_DEPOSIT_PAID,
  PURCHASE_ORDER_STATUS_DRAFT,
  PURCHASE_ORDER_STATUS_READY_FOR_PRODUCTION,
  PURCHASE_ORDER_STATUS_IN_PRODUCTION,
  PURCHASE_ORDER_STATUS_PAID,
  PURCHASE_ORDER_STATUS_PAYMENT_PENDING,
  PURCHASE_ORDER_STATUS_PENDING_APPROVAL,
  PURCHASE_ORDER_STATUS_PENDING_CONFIRMATION,
  PURCHASE_ORDER_STATUS_PENDING_SUPPLIER_REVIEW,
  PURCHASE_ORDER_STATUS_RECEIVED,
  PURCHASE_ORDER_STATUS_SHIPPED,
  PURCHASE_ORDER_STATUS_SPLIT,
  PURCHASE_ORDER_STATUS_UNDER_REVIEW,
  PURCHASE_ORDER_PAYMENT_STATUS_PENDING,
  PURCHASE_ORDER_PAYMENT_STATUS_DEPOSIT_PAID,
  PURCHASE_ORDER_PAYMENT_STATUS_PAID,
} from "../../../../constants/OrderConstant";
import { addLogEntry } from "../../../../utils/logging";
import { useTranslation } from "react-i18next";
import urls from "../../../../urls";
import styled from "styled-components";
import { formatPrice, formatPriceUsd } from "../../../../functions/formatters";
import { useSuppliers } from "../../../../data/hooks";

const { Panel } = Collapse;
const { TextArea } = Input;

const PurchaseOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const firestore = useFirestore();
  const firebase = useFirebase();
  const { t } = useTranslation();
  const [reviewForm] = Form.useForm();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [order, setOrder] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProducts, setEditedProducts] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm] = Form.useForm();
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [uploadingPayment, setUploadingPayment] = useState(false);
  const [showSplitOrderModal, setShowSplitOrderModal] = useState(false);
  const [splitOrderForm] = Form.useForm();
  const [splitOrderProducts, setSplitOrderProducts] = useState([]);

  const profile = useSelector((state) => state.firebaseReducer.profile);
  const { email } = profile;
  const products = useSelector((state) => state.firestoreReducer.ordered.products);
  const { suppliers } = useSuppliers();

  useEffect(() => {
    // Set up real-time listener for order updates
    const unsubscribe = firestore
      .collection(FIRESTORE_PURCHASE_ORDERS_TABLE)
      .doc(id)
      .onSnapshot(
        (doc) => {
          if (doc.exists) {
            setOrder({ id: doc.id, ...doc.data() });
          } else {
            message.error(t("notifications.purchase_order_not_found"));
            navigate(urls.purchaseOrdersList);
          }
          setLoading(false);
        },
        (error) => {
          console.error("Error listening to purchase order:", error);
          message.error(t("notifications.purchase_order_load_failed"));
          setLoading(false);
        },
      );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [id]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      const doc = await firestore.collection(FIRESTORE_PURCHASE_ORDERS_TABLE).doc(id).get();

      if (doc.exists) {
        setOrder({ id: doc.id, ...doc.data() });
      } else {
        message.error(t("notifications.purchase_order_not_found"));
        navigate(urls.purchaseOrdersList);
      }
    } catch (error) {
      console.error("Error loading purchase order:", error);
      message.error(t("notifications.purchase_order_load_failed"));
    } finally {
      setLoading(false);
    }
  };

  const getPurchaseOrderStatusColor = (status) => {
    if (status === PURCHASE_ORDER_STATUS_COMPLETED) return "green";
    else if (status === PURCHASE_ORDER_STATUS_DRAFT) return "default";
    else if (status === PURCHASE_ORDER_STATUS_PENDING_CONFIRMATION) return "gold";
    else if (status === PURCHASE_ORDER_STATUS_PENDING_APPROVAL) return "volcano";
    else if (status === PURCHASE_ORDER_STATUS_PENDING_SUPPLIER_REVIEW) return "gold";
    else if (status === PURCHASE_ORDER_STATUS_PAYMENT_PENDING) return "orange";
    else if (status === PURCHASE_ORDER_STATUS_DEPOSIT_PAID) return "cyan";
    else if (status === PURCHASE_ORDER_STATUS_PAID) return "lime";
    else if (status === PURCHASE_ORDER_STATUS_READY_FOR_PRODUCTION) return "geekblue";
    else if (status === PURCHASE_ORDER_STATUS_IN_PRODUCTION) return "blue";
    else if (status === PURCHASE_ORDER_STATUS_SHIPPED) return "orange";
    else if (status === PURCHASE_ORDER_STATUS_RECEIVED) return "cyan";
    else if (status === PURCHASE_ORDER_STATUS_UNDER_REVIEW) return "purple";
    else if (status === PURCHASE_ORDER_STATUS_SPLIT) return "magenta";
    else return "green";
  };

  const handleOpenReviewModal = () => {
    // Initialize form with products data
    const initialValues = {
      products: order.products.map((product) => ({
        productId: product.productId,
        productName: product.productName,
        productColor: product.productColor,
        productCode: product.productCode,
        expectedAmount: product.adjustedAmount || product.amount,
        receivedAmount: product.adjustedAmount || product.amount,
        discrepancyReason: "",
      })),
    };
    reviewForm.setFieldsValue(initialValues);
    setShowReviewModal(true);
  };

  const handleSubmitReview = async () => {
    try {
      const values = await reviewForm.validateFields();
      setSaving(true);

      const receivedProducts = values.products.map((product) => ({
        productId: product.productId,
        productCode: product.productCode,
        receivedAmount: product.receivedAmount,
        ...(product.receivedAmount !== product.expectedAmount && product.discrepancyReason
          ? { discrepancyReason: product.discrepancyReason }
          : {}),
      }));

      const oldLog = order.log || [];
      const newLog = addLogEntry(oldLog, {
        action: "items_received",
        user: email,
        metadata: {
          totalReceived: receivedProducts.reduce((sum, p) => sum + p.receivedAmount, 0),
          discrepancies: receivedProducts.filter((p) => p.discrepancyReason).length,
        },
      });

      await firestore.collection(FIRESTORE_PURCHASE_ORDERS_TABLE).doc(id).update({
        receivedProducts,
        log: newLog,
      });

      message.success(t("notifications.review_submitted_successfully"));
      setShowReviewModal(false);
      await loadOrder();
    } catch (error) {
      console.error("Error submitting review:", error);
      message.error(t("notifications.review_submission_failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleStartEditing = () => {
    setIsEditing(true);
    setEditedProducts(JSON.parse(JSON.stringify(order.products))); // Deep copy
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    setEditedProducts([]);
  };

  const handleProductChange = (index, field, value) => {
    const updated = [...editedProducts];
    updated[index] = { ...updated[index], [field]: value };
    setEditedProducts(updated);
  };

  const handleRemoveProduct = (index) => {
    Modal.confirm({
      title: t("remove_product"),
      content: t("remove_product_confirm"),
      okText: t("yes_remove"),
      cancelText: t("cancel"),
      okType: "danger",
      onOk: () => {
        const updated = editedProducts.filter((_, i) => i !== index);
        setEditedProducts(updated);
      },
    });
  };

  const handleAddProduct = (productId) => {
    const product = products.find((p) => p.id === productId);
    if (!product) return;

    const newProduct = {
      productId: product.id,
      productName: product.name,
      productCode: product.code || "",
      productBarcode: product.barcode || "",
      productColor: product.color || "",
      productDimensions: product.dimensions || null,
      productImages: product.images || [],
      innerPackagingQuantity: product.innerPackagingQuantity || 0,
      masterPackagingQuantity: product.masterPackagingQuantity || 0,
      amount: 0,
      price: product.purchasePrice || 0,
      priority: false,
    };

    setEditedProducts([...editedProducts, newProduct]);
    message.success(`${product.name} ${t("added")}`);
  };

  const handleSaveAndSendToSupplier = async () => {
    try {
      setSaving(true);

      // Recalculate totalProducts and totalPrice based on edited products
      const totalProducts = editedProducts.reduce((sum, product) => {
        const amount = product.adjustedAmount !== undefined ? product.adjustedAmount : product.amount;
        return sum + amount;
      }, 0);

      const totalPrice = editedProducts.reduce((sum, product) => {
        const price = product.adjustedPrice !== undefined ? product.adjustedPrice : product.price || 0;
        const amount = product.adjustedAmount !== undefined ? product.adjustedAmount : product.amount;
        return sum + price * amount;
      }, 0);

      const oldLog = order.log || [];
      const newLog = addLogEntry(oldLog, {
        action: "bombyxx_modifications",
        user: email,
        metadata: {
          message: "Bombyxx made modifications based on CBM requirements",
          totalProducts,
          totalPrice,
        },
      });

      await firestore.collection(FIRESTORE_PURCHASE_ORDERS_TABLE).doc(id).update({
        products: editedProducts,
        totalProducts,
        totalPrice,
        status: PURCHASE_ORDER_STATUS_PENDING_SUPPLIER_REVIEW,
        log: newLog,
      });

      message.success(t("changes_saved_sent_supplier"));
      setIsEditing(false);
      await loadOrder();
    } catch (error) {
      console.error("Error saving changes:", error);
      message.error(t("error_saving_changes"));
    } finally {
      setSaving(false);
    }
  };

  const updateProductPricesAndPackaging = async (orderProducts) => {
    const updatePromises = orderProducts.map(async (orderProduct) => {
      // Only update products that have a productId (not draft products)
      if (!orderProduct.productId) return;

      const product = products.find((p) => p.id === orderProduct.productId);
      if (!product) return;

      const updates = {};
      let hasUpdates = false;

      // Check if purchase price differs
      if (orderProduct.price !== undefined && orderProduct.price !== product.purchasePrice) {
        updates.purchasePrice = orderProduct.price;
        hasUpdates = true;
      }

      // Check if inner packaging quantity differs
      if (
        orderProduct.innerPackagingQuantity !== undefined &&
        orderProduct.innerPackagingQuantity !== product.innerPackagingQuantity
      ) {
        updates.innerPackagingQuantity = orderProduct.innerPackagingQuantity;
        hasUpdates = true;
      }

      // Check if master packaging quantity differs
      if (
        orderProduct.masterPackagingQuantity !== undefined &&
        orderProduct.masterPackagingQuantity !== product.masterPackagingQuantity
      ) {
        updates.masterPackagingQuantity = orderProduct.masterPackagingQuantity;
        hasUpdates = true;
      }

      // Only update if there are changes
      if (hasUpdates) {
        updates.updatedAt = dayjs().unix();
        updates.updatedBy = email;

        await firestore.collection(FIRESTORE_PRODUCTS_TABLE).doc(orderProduct.productId).update(updates);
      }
    });

    await Promise.all(updatePromises);
  };

  const handleApproveChanges = async () => {
    try {
      setSaving(true);

      // Apply adjustedAmount, adjustedPrice, adjustedPackaging and adjustedDimensions to base values
      const updatedProducts = order.products.map((product) => {
        const updates = { ...product };

        if (product.adjustedAmount !== undefined && product.adjustedAmount !== product.amount) {
          updates.amount = product.adjustedAmount;
        }

        if (product.adjustedPrice !== undefined && product.adjustedPrice !== product.price) {
          updates.price = product.adjustedPrice;
        }

        if (
          product.adjustedInnerPackagingQuantity !== undefined &&
          product.adjustedInnerPackagingQuantity !== product.innerPackagingQuantity
        ) {
          updates.innerPackagingQuantity = product.adjustedInnerPackagingQuantity;
        }

        if (
          product.adjustedMasterPackagingQuantity !== undefined &&
          product.adjustedMasterPackagingQuantity !== product.masterPackagingQuantity
        ) {
          updates.masterPackagingQuantity = product.adjustedMasterPackagingQuantity;
        }

        if (
          product.adjustedProductDimensions &&
          JSON.stringify(product.adjustedProductDimensions) !== JSON.stringify(product.productDimensions)
        ) {
          updates.productDimensions = product.adjustedProductDimensions;
        }

        // Clear adjusted fields after approval
        delete updates.adjustedAmount;
        delete updates.adjustedPrice;
        delete updates.adjustedInnerPackagingQuantity;
        delete updates.adjustedMasterPackagingQuantity;
        delete updates.adjustedProductDimensions;

        return updates;
      });

      // Recalculate totalProducts and totalPrice based on updated products
      const totalProducts = updatedProducts.reduce((sum, product) => sum + product.amount, 0);
      const totalPrice = updatedProducts.reduce((sum, product) => {
        const price = product.price || 0;
        const amount = product.amount || 0;
        return sum + price * amount;
      }, 0);

      const oldLog = order.log || [];
      const newLog = addLogEntry(oldLog, {
        action: "changes_approved",
        user: email,
        metadata: {
          message: "Supplier changes approved by Bombyxx",
          totalProducts,
          totalPrice,
        },
      });

      await firestore.collection(FIRESTORE_PURCHASE_ORDERS_TABLE).doc(id).update({
        products: updatedProducts,
        totalProducts,
        totalPrice,
        status: PURCHASE_ORDER_STATUS_READY_FOR_PRODUCTION,
        paymentStatus: PURCHASE_ORDER_PAYMENT_STATUS_PENDING,
        log: newLog,
      });

      // Update product master data (prices and packaging) in products collection
      await updateProductPricesAndPackaging(updatedProducts);

      message.success(t("notifications.changes_approved"));
      await loadOrder();
    } catch (error) {
      console.error("Error approving changes:", error);
      message.error(t("notifications.changes_approval_failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateOrderStatus = async (newStatus) => {
    try {
      setSaving(true);
      const oldLog = order.log || [];
      const newLog = addLogEntry(oldLog, {
        action: "status_changed",
        user: email,
        oldValue: order.status,
        newValue: newStatus,
      });

      const updateData = {
        status: newStatus,
        log: newLog,
      };

      if (newStatus === PURCHASE_ORDER_STATUS_RECEIVED) {
        updateData.receivedAt = dayjs().unix();
      }

      await firestore.collection(FIRESTORE_PURCHASE_ORDERS_TABLE).doc(id).update(updateData);

      message.success(t("notifications.purchase_order_status_update_successful"));
      await loadOrder();
    } catch (error) {
      console.error("Error updating order status:", error);
      message.error(t("notifications.purchase_order_status_update_failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleMarkAsPaid = async () => {
    try {
      const values = await paymentForm.validateFields();
      setUploadingPayment(true);

      let paymentProofUrl = null;

      // Upload payment proof if provided
      if (paymentProofFile) {
        const storageRef = firebase.storage().ref();
        const fileRef = storageRef.child(`purchaseOrders/${id}/payment-proof-${Date.now()}-${paymentProofFile.name}`);
        await fileRef.put(paymentProofFile);
        paymentProofUrl = await fileRef.getDownloadURL();
      }

      // Create new payment object
      const newPayment = {
        amount: values.paidAmount,
        exchangeRate: values.exchangeRate || null,
        amountEur: values.exchangeRate ? values.paidAmount * values.exchangeRate : null,
        paidAt: dayjs().unix(),
        paidBy: email,
        notes: values.paymentNotes || "",
      };

      if (paymentProofUrl) {
        newPayment.proofUrl = paymentProofUrl;
      }

      // Get existing payments or create new array
      const existingPayments = order.payments || [];
      const updatedPayments = [...existingPayments, newPayment];

      // Calculate total paid amount
      const totalPaid = updatedPayments.reduce((sum, payment) => sum + payment.amount, 0);
      const orderTotal = order.invoice?.totalAmount || order.totalPrice;
      const isFullyPaid = totalPaid >= orderTotal;

      const oldLog = order.log || [];
      const newLog = addLogEntry(oldLog, {
        action: "payment_added",
        user: email,
        metadata: {
          paymentAmount: values.paidAmount,
          exchangeRate: values.exchangeRate || null,
          amountEur: values.exchangeRate ? values.paidAmount * values.exchangeRate : null,
          totalPaid: totalPaid,
          remainingAmount: orderTotal - totalPaid,
          paymentProofUrl: paymentProofUrl,
          paymentNotes: values.paymentNotes || "",
        },
      });

      const updateData = {
        payments: updatedPayments,
        totalPaid: totalPaid,
        log: newLog,
      };

      // Payment status logic (separate from order status)
      if (isFullyPaid) {
        // Fully paid - set paymentStatus to paid
        updateData.paymentStatus = PURCHASE_ORDER_PAYMENT_STATUS_PAID;
        updateData.fullyPaidAt = dayjs().unix();
      } else if (existingPayments.length === 0) {
        // First payment (deposit) - set paymentStatus to deposit_paid
        updateData.paymentStatus = PURCHASE_ORDER_PAYMENT_STATUS_DEPOSIT_PAID;
        updateData.depositPaidAt = dayjs().unix();
      }
      // If already deposit_paid and adding another payment (but not fully paid), paymentStatus stays deposit_paid

      // Initialize paymentStatus if it doesn't exist yet
      if (!order.paymentStatus) {
        updateData.paymentStatus = updateData.paymentStatus || PURCHASE_ORDER_PAYMENT_STATUS_PENDING;
      }

      await firestore.collection(FIRESTORE_PURCHASE_ORDERS_TABLE).doc(id).update(updateData);

      message.success(t("payment_added_amount", { amount: formatPriceUsd(values.paidAmount) }));
      if (isFullyPaid) {
        message.success(t("order_fully_paid"));
      }
      setShowPaymentModal(false);
      paymentForm.resetFields();
      setPaymentProofFile(null);
      await loadOrder();
    } catch (error) {
      console.error("Error adding payment:", error);
      message.error(t("error_adding_payment"));
    } finally {
      setUploadingPayment(false);
    }
  };

  const handleCompleteOrder = async () => {
    Modal.confirm({
      title: t("confirm_complete_order"),
      icon: <ExclamationCircleOutlined />,
      content: t("confirm_complete_order_description"),
      okText: t("yes"),
      cancelText: t("no"),
      onOk: async () => {
        try {
          setSaving(true);
          const oldLog = order.log || [];
          const newLog = addLogEntry(oldLog, {
            action: "status_changed",
            user: email,
            oldValue: order.status,
            newValue: PURCHASE_ORDER_STATUS_COMPLETED,
          });

          await firestore.collection(FIRESTORE_PURCHASE_ORDERS_TABLE).doc(id).update({
            status: PURCHASE_ORDER_STATUS_COMPLETED,
            completedAt: dayjs().unix(),
            log: newLog,
          });

          message.success(t("notifications.purchase_order_completed"));
          await loadOrder();
        } catch (error) {
          console.error("Error completing order:", error);
          message.error(t("notifications.purchase_order_complete_failed"));
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const handleOpenSplitOrderModal = () => {
    setSplitOrderProducts([]);
    splitOrderForm.resetFields();
    setShowSplitOrderModal(true);
  };

  const handleAddProductToSplitOrder = (productId) => {
    const product = products.find((p) => p.id === productId);
    if (!product) return;

    const newProduct = {
      productId: product.id,
      productName: product.name,
      productCode: product.code || "",
      productColor: product.color || "",
      amount: 0,
      price: product.purchasePrice || 0,
      cbm: product.cbm || 0,
    };

    setSplitOrderProducts([...splitOrderProducts, newProduct]);
    message.success(`${product.name} ${t("added")}`);
  };

  const handleRemoveProductFromSplitOrder = (index) => {
    const updated = [...splitOrderProducts];
    updated.splice(index, 1);
    setSplitOrderProducts(updated);
  };

  const handleSplitOrderProductChange = (index, field, value) => {
    const updated = [...splitOrderProducts];
    updated[index][field] = value;
    setSplitOrderProducts(updated);
  };

  const handleCreateSplitOrder = async () => {
    try {
      const values = await splitOrderForm.validateFields();
      setSaving(true);

      // Calculate totals
      const totalAmount = splitOrderProducts.reduce((sum, p) => sum + p.amount * p.price, 0);
      const totalCbm = splitOrderProducts.reduce((sum, p) => sum + p.amount * (p.cbm || 0), 0);

      // Generate child order number
      const existingChildOrders = order.childOrders || [];
      const childSuffix = String.fromCharCode(65 + existingChildOrders.length); // A, B, C, etc.
      const childOrderNumber = `${order.orderNumber}-${childSuffix}`;

      // Create child order data
      const childOrderData = {
        orderNumber: childOrderNumber,
        parentOrderId: id,
        parentOrderNumber: order.orderNumber,
        status: PURCHASE_ORDER_STATUS_DRAFT,
        supplierId: order.supplierId,
        supplierName: order.supplierName,
        products: splitOrderProducts,
        totalProducts: splitOrderProducts.length,
        totalPrice: totalAmount,
        cubicMeters: totalCbm,
        exchangeRate: order.exchangeRate,
        isSplitOrder: true,
        splitNotes: values.notes || "",
        createdBy: email,
        orderDate: dayjs().unix(),
        log: [
          {
            timestamp: dayjs().unix(),
            action: "split_order_created",
            user: email,
            metadata: {
              parentOrderId: id,
              parentOrderNumber: order.orderNumber,
              reason: values.notes || t("extra_products_added"),
            },
          },
        ],
      };

      // Create child order in Firestore
      const childOrderRef = await firestore.collection(FIRESTORE_PURCHASE_ORDERS_TABLE).add(childOrderData);

      // Update parent order
      const updatedChildOrders = [...existingChildOrders, childOrderRef.id];
      const oldLog = order.log || [];
      const newLog = addLogEntry(oldLog, {
        action: "split_order_created",
        user: email,
        metadata: {
          childOrderId: childOrderRef.id,
          childOrderNumber: childOrderNumber,
          totalProducts: splitOrderProducts.length,
          reason: values.notes || "",
        },
      });

      await firestore.collection(FIRESTORE_PURCHASE_ORDERS_TABLE).doc(id).update({
        status: PURCHASE_ORDER_STATUS_SPLIT,
        childOrders: updatedChildOrders,
        isSplit: true,
        log: newLog,
      });

      message.success(t("split_order_created", { orderNumber: childOrderNumber }));
      setShowSplitOrderModal(false);
      setSplitOrderProducts([]);
      splitOrderForm.resetFields();

      // Navigate to the new child order
      navigate(`${urls.purchaseOrdersList}/${childOrderRef.id}`);
    } catch (error) {
      console.error("Error creating split order:", error);
      message.error(t("error_creating_split_order"));
    } finally {
      setSaving(false);
    }
  };

  const calculateSplitOrderTotals = () => {
    const totalAmount = splitOrderProducts.reduce((sum, p) => sum + p.amount * p.price, 0);
    const totalCbm = splitOrderProducts.reduce((sum, p) => sum + p.amount * (p.cbm || 0), 0);
    return { totalAmount, totalCbm };
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const isReceived = order.status === PURCHASE_ORDER_STATUS_RECEIVED;
  const isShipped = order.status === PURCHASE_ORDER_STATUS_SHIPPED;
  const isUnderReview = order.status === PURCHASE_ORDER_STATUS_UNDER_REVIEW;
  const isPendingApproval = order.status === PURCHASE_ORDER_STATUS_PENDING_APPROVAL;
  const isReadyForProduction = order.status === PURCHASE_ORDER_STATUS_READY_FOR_PRODUCTION;
  const isPaymentPending = order.paymentStatus === PURCHASE_ORDER_PAYMENT_STATUS_PENDING;
  const isDepositPaid = order.paymentStatus === PURCHASE_ORDER_PAYMENT_STATUS_DEPOSIT_PAID;
  const isFullyPaid = order.paymentStatus === PURCHASE_ORDER_PAYMENT_STATUS_PAID;
  const isInProduction = order.status === PURCHASE_ORDER_STATUS_IN_PRODUCTION;
  const hasReview = order.receivedProducts && order.receivedProducts.length > 0;
  const canComplete = isUnderReview && hasReview;

  return (
    <div style={{ padding: "24px" }}>
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "center",
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? 12 : 0,
            }}
          >
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(urls.purchaseOrdersList)}
              style={{ width: isMobile ? "100%" : "auto" }}
            >
              {t("back")}
            </Button>
            <Space direction={isMobile ? "vertical" : "horizontal"} style={{ width: isMobile ? "100%" : "auto" }}>
              {isPendingApproval && !isEditing && (
                <>
                  <Button onClick={handleStartEditing} style={{ width: isMobile ? "100%" : "auto" }}>
                    {t("edit")}
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleApproveChanges}
                    loading={saving}
                    style={{ width: isMobile ? "100%" : "auto" }}
                  >
                    {t("approve_changes")}
                  </Button>
                </>
              )}
              {isPendingApproval && isEditing && (
                <>
                  <Button onClick={handleCancelEditing} style={{ width: isMobile ? "100%" : "auto" }}>
                    {t("cancel")}
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleSaveAndSendToSupplier}
                    loading={saving}
                    style={{ width: isMobile ? "100%" : "auto" }}
                  >
                    {t("save_and_send_supplier")}
                  </Button>
                </>
              )}
              {(isPaymentPending ||
                isDepositPaid ||
                (!isFullyPaid &&
                  order.payments &&
                  order.payments.length > 0 &&
                  order.totalPaid < (order.invoice?.totalAmount || order.totalPrice))) && (
                <Button
                  type="primary"
                  onClick={() => setShowPaymentModal(true)}
                  loading={saving}
                  style={{ width: isMobile ? "100%" : "auto" }}
                >
                  {t("add_payment")}
                </Button>
              )}
              {isReadyForProduction && (
                <Button
                  type="primary"
                  onClick={() => handleUpdateOrderStatus(PURCHASE_ORDER_STATUS_IN_PRODUCTION)}
                  loading={saving}
                  style={{ width: isMobile ? "100%" : "auto" }}
                >
                  {t("start_production")}
                </Button>
              )}
              {isShipped && (
                <Button
                  type="primary"
                  onClick={() => handleUpdateOrderStatus(PURCHASE_ORDER_STATUS_RECEIVED)}
                  loading={saving}
                  style={{ width: isMobile ? "100%" : "auto" }}
                >
                  {t("set_to_received")}
                </Button>
              )}
              {isReceived && (
                <Button
                  type="primary"
                  onClick={() => handleUpdateOrderStatus(PURCHASE_ORDER_STATUS_UNDER_REVIEW)}
                  loading={saving}
                  style={{ width: isMobile ? "100%" : "auto" }}
                >
                  {t("set_to_under_review")}
                </Button>
              )}
              {isUnderReview && !hasReview && (
                <Button
                  type="primary"
                  onClick={handleOpenReviewModal}
                  loading={saving}
                  style={{ width: isMobile ? "100%" : "auto" }}
                >
                  {t("review_received_items")}
                </Button>
              )}
              {canComplete && (
                <Button
                  type="primary"
                  onClick={handleCompleteOrder}
                  loading={saving}
                  style={{ width: isMobile ? "100%" : "auto" }}
                >
                  {t("complete_order")}
                </Button>
              )}
              {isInProduction && !order.isSplitOrder && (
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={handleOpenSplitOrderModal}
                  style={{ width: isMobile ? "100%" : "auto" }}
                >
                  {t("add_products")}
                </Button>
              )}
            </Space>
          </div>
        }
      >
        {isPendingApproval && (
          <div
            style={{
              padding: "16px",
              marginBottom: "16px",
              background: "#fff7e6",
              border: "1px solid #ffd591",
              borderRadius: "4px",
            }}
          >
            <p style={{ margin: 0, marginBottom: 8, fontWeight: "bold" }}>⚠️ {t("supplier_made_changes")}</p>
            <p style={{ margin: 0, fontSize: "14px" }}>{t("check_changes_approve")}</p>
          </div>
        )}
        <Descriptions bordered column={isMobile ? 1 : 2} size={isMobile ? "small" : "default"}>
          <Descriptions.Item label={t("order.status")}>
            <Tag color={getPurchaseOrderStatusColor(order.status)}>{t(`order.${order.status.toLowerCase()}`)}</Tag>
          </Descriptions.Item>
          {order.paymentStatus && (
            <Descriptions.Item label={t("payment_status")}>
              <Tag
                color={
                  order.paymentStatus === PURCHASE_ORDER_PAYMENT_STATUS_PAID
                    ? "green"
                    : order.paymentStatus === PURCHASE_ORDER_PAYMENT_STATUS_DEPOSIT_PAID
                      ? "cyan"
                      : "orange"
                }
              >
                {t(`order.${order.paymentStatus.toLowerCase()}`)}
              </Tag>
            </Descriptions.Item>
          )}
          <Descriptions.Item label={t("supplier.supplier")} span={order.paymentStatus ? 1 : 2}>
            {order.supplierName}
          </Descriptions.Item>
          <Descriptions.Item label={t("order.order_date")}>
            {order.orderDate ? dayjs.unix(order.orderDate).format("DD-MM-YYYY HH:mm") : "-"}
          </Descriptions.Item>
          <Descriptions.Item label={t("total_products")}>{order.totalProducts}</Descriptions.Item>
          <Descriptions.Item label={t("total_price")}>
            {(() => {
              const additionalCostsTotal =
                order.invoice?.additionalCosts?.reduce((sum, cost) => sum + cost.amount, 0) || 0;
              const productsTotal = order.totalPrice ?? 0;
              const total = productsTotal + additionalCostsTotal;

              if (additionalCostsTotal > 0) {
                return (
                  <div>
                    <div>
                      {order.currency === "IDR"
                        ? `IDR ${productsTotal.toLocaleString("id-ID")}`
                        : formatPriceUsd(productsTotal)}{" "}
                      <span style={{ color: "#666", fontSize: "12px" }}>(producten)</span>
                    </div>
                    <div style={{ marginTop: 4 }}>
                      {order.currency === "IDR"
                        ? `IDR ${additionalCostsTotal.toLocaleString("id-ID")}`
                        : formatPriceUsd(additionalCostsTotal)}{" "}
                      <span style={{ color: "#fa8c16", fontSize: "12px" }}>(extra kosten)</span>
                    </div>
                    <div style={{ marginTop: 4, paddingTop: 4, borderTop: "1px solid #d9d9d9", fontWeight: "bold" }}>
                      {order.currency === "IDR"
                        ? `IDR ${(order.totalPrice ?? 0).toLocaleString("id-ID")}`
                        : formatPriceUsd(total ?? 0)}{" "}
                    </div>
                  </div>
                );
              }

              return order.currency === "IDR"
                ? `IDR ${(order.totalPrice ?? 0).toLocaleString("id-ID")}`
                : formatPriceUsd(order.totalPrice ?? 0);
            })()}
          </Descriptions.Item>
          <Descriptions.Item label={t("currency")}>
            <Tag>{order.currency || "USD"}</Tag>
          </Descriptions.Item>
          {order.currency !== "IDR" && (
            <>
              <Descriptions.Item label={t("exchange_rate_usd_eur")}>
                {order.exchangeRate ? `1 USD = ${order.exchangeRate.toFixed(4)} EUR` : "-"}
              </Descriptions.Item>
              <Descriptions.Item label={t("total_price_eur")}>
                {order.totalPrice && order.exchangeRate
                  ? `€ ${formatPrice(order.totalPrice * order.exchangeRate)}`
                  : "-"}
              </Descriptions.Item>
            </>
          )}
          <Descriptions.Item label={t("order.ordered_by")} span={2}>
            {order.createdBy || "-"}
          </Descriptions.Item>
          {order.etd && (
            <Descriptions.Item label={t("etd")}>{dayjs.unix(order.etd).format("DD-MM-YYYY")}</Descriptions.Item>
          )}
          {order.orderedAt && (
            <Descriptions.Item label={t("ordered_at")}>
              {dayjs.unix(order.orderedAt).format("DD-MM-YYYY HH:mm")}
            </Descriptions.Item>
          )}
          {order.receivedAt && (
            <Descriptions.Item label={t("received_at")}>
              {dayjs.unix(order.receivedAt).format("DD-MM-YYYY HH:mm")}
            </Descriptions.Item>
          )}
          {order.completedAt && (
            <Descriptions.Item label={t("completed_at")}>
              {dayjs.unix(order.completedAt).format("DD-MM-YYYY HH:mm")}
            </Descriptions.Item>
          )}
          {order.trackingCode && (
            <Descriptions.Item label={t("tracking_code")} span={2}>
              <a href={order.trackingCode} target="_blank" rel="noopener noreferrer">
                {order.trackingCode}
              </a>
            </Descriptions.Item>
          )}
          {order.payments && order.payments.length > 0 && (
            <Descriptions.Item label="Betalingen" span={2}>
              <Space direction="vertical" style={{ width: "100%" }}>
                {order.payments.map((payment, index) => (
                  <Card key={index} size="small" style={{ backgroundColor: "#f6ffed" }}>
                    <Space direction="vertical" style={{ width: "100%" }} size="small">
                      <div>
                        <strong style={{ fontSize: 16, color: "#52c41a" }}>{formatPriceUsd(payment.amount)}</strong>
                        <span style={{ marginLeft: 8, color: "#666", fontSize: 14 }}>
                          op {dayjs.unix(payment.paidAt).format("DD-MM-YYYY HH:mm")}
                        </span>
                      </div>
                      {payment.notes && (
                        <div style={{ fontSize: 13, color: "#666" }}>
                          <strong>{t("notes")}:</strong> {payment.notes}
                        </div>
                      )}
                      {payment.proofUrl && (
                        <div>
                          <a href={payment.proofUrl} target="_blank" rel="noopener noreferrer">
                            <Button size="small" icon={<UploadOutlined />}>
                              {t("view_payment_proof")}
                            </Button>
                          </a>
                        </div>
                      )}
                    </Space>
                  </Card>
                ))}
                <div style={{ marginTop: 8, padding: 8, backgroundColor: "#e6f7ff", borderRadius: 4 }}>
                  <strong>{t("total_paid")}:</strong>{" "}
                  <span style={{ fontSize: 16, fontWeight: "bold", color: "#1890ff" }}>
                    {formatPriceUsd(order.totalPaid || 0)}
                  </span>
                  {" / "}
                  <span>{formatPriceUsd(order.invoice?.totalAmount || order.totalPrice)}</span>
                  {order.totalPaid < (order.invoice?.totalAmount || order.totalPrice) && (
                    <span style={{ marginLeft: 8, color: "#ff4d4f" }}>
                      ({t("still_to_pay")}:{" "}
                      {formatPriceUsd((order.invoice?.totalAmount || order.totalPrice) - order.totalPaid)})
                    </span>
                  )}
                  {order.totalPaid >= (order.invoice?.totalAmount || order.totalPrice) && (
                    <Tag color="green" style={{ marginLeft: 8 }}>
                      ✓ {t("fully_paid")}
                    </Tag>
                  )}
                </div>
              </Space>
            </Descriptions.Item>
          )}
          {order.cubicMeters && (
            <Descriptions.Item label={t("cubic_meters")} span={2}>
              <span style={{ fontSize: 16, fontWeight: "bold", marginRight: 8 }}>{order.cubicMeters} m³</span>
              {(() => {
                const cbm = order.cubicMeters;
                const containerCapacity = 68;
                const numContainers = Math.ceil(cbm / containerCapacity);
                const remainder = cbm % containerCapacity;
                const isFullContainers = remainder === 0;
                const totalCapacity = numContainers * containerCapacity;
                const spaceLeft = totalCapacity - cbm;

                if (isFullContainers) {
                  return (
                    <Tag color="green">
                      ✓ {numContainers} {numContainers > 1 ? t("full_containers_plural") : t("full_containers")}
                    </Tag>
                  );
                } else if (remainder >= 60) {
                  return (
                    <Tag color="orange">
                      {t("container_almost_full", {
                        num: numContainers,
                        used: remainder,
                        left: containerCapacity - remainder,
                      })}
                    </Tag>
                  );
                } else {
                  return (
                    <Tag color="orange">
                      {t("container_suboptimal", { num: numContainers, used: remainder, add: spaceLeft })}
                    </Tag>
                  );
                }
              })()}
            </Descriptions.Item>
          )}
          {order.parentOrderNumber && (
            <Descriptions.Item label={t("parent_order")} span={2}>
              <a onClick={() => navigate(`${urls.purchaseOrdersList}/${order.parentOrderId}`)}>
                {order.parentOrderNumber}
              </a>
            </Descriptions.Item>
          )}
          {order.isSplit && order.childOrders && order.childOrders.length > 0 && (
            <Descriptions.Item label={t("child_orders")} span={2}>
              <Space>
                {order.childOrders.map((childId, index) => (
                  <Tag
                    key={childId}
                    color="magenta"
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate(`${urls.purchaseOrdersList}/${childId}`)}
                  >
                    {order.orderNumber}-{String.fromCharCode(65 + index)}
                  </Tag>
                ))}
              </Space>
            </Descriptions.Item>
          )}
        </Descriptions>

        {order.comments && (
          <div style={{ marginTop: 24 }}>
            <h3>{t("comments")}</h3>
            <Card size="small" style={{ background: "#fafafa" }}>
              {order.comments}
            </Card>
          </div>
        )}

        {order.images && order.images.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <h3>{t("images")}</h3>
            <Space size="middle" wrap>
              {order.images.map((img, idx) => (
                <a key={idx} href={img} target="_blank" rel="noopener noreferrer">
                  <img
                    src={img}
                    alt={`Order image ${idx + 1}`}
                    style={{
                      width: 120,
                      height: 120,
                      objectFit: "cover",
                      borderRadius: 4,
                      border: "1px solid #d9d9d9",
                    }}
                  />
                </a>
              ))}
            </Space>
          </div>
        )}

        <h5 style={{ marginTop: 24 }}>
          {t("products")}
          {isEditing && order.cubicMeters && (
            <span style={{ marginLeft: 16 }}>
              {(() => {
                const cbm = order.cubicMeters;
                const containerCapacity = 68;
                const numContainers = Math.ceil(cbm / containerCapacity);
                const remainder = cbm % containerCapacity;
                const isFullContainers = remainder === 0;

                return (
                  <Tag color={isFullContainers ? "green" : "orange"} style={{ fontSize: 14, padding: "4px 12px" }}>
                    CBM: {cbm} m³ ({numContainers} container{numContainers > 1 ? "s" : ""})
                    {isFullContainers ? " ✓" : ` - ${containerCapacity - remainder} m³ ${t("container_shortage")}`}
                  </Tag>
                );
              })()}
            </span>
          )}
        </h5>

        {isEditing && (
          <Card style={{ marginBottom: 16, backgroundColor: "#f6ffed", borderColor: "#b7eb8f" }}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <div style={{ fontWeight: "bold" }}>
                <PlusOutlined /> {t("add_product")}
              </div>
              <Select
                showSearch
                style={{ width: "100%" }}
                placeholder={t("search_select_product")}
                optionFilterProp="children"
                onChange={handleAddProduct}
                value={null}
                filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                options={products
                  ?.filter((p) => !editedProducts.some((ep) => ep.productId === p.id))
                  .map((p) => ({
                    value: p.id,
                    label: `${p.name}${p.color ? ` (${p.color})` : ""} - ${p.code || ""}`,
                  }))}
              />
            </Space>
          </Card>
        )}

        {isMobile && !isEditing ? (
          <Space direction="vertical" style={{ width: "100%" }} size={12}>
            {order.products.map((item, index) => {
              const receivedItem = order.receivedProducts?.find((rp) => rp.productId === item.productId);
              const hasDiscrepancy =
                receivedItem && receivedItem.receivedAmount !== (item.adjustedAmount || item.amount);

              return (
                <MobileProductCard key={index}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: 12,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: 0, marginBottom: 4, fontSize: 16, fontWeight: 600 }}>
                        {item.productName}
                        {item.productColor && (
                          <span style={{ color: "#666", marginLeft: 4 }}>({item.productColor})</span>
                        )}
                      </h4>
                      <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>{item.productCode || "-"}</div>
                      {item.productDraftId && (
                        <Tag color="gold" size="small">
                          {t("product_draft.new_product")}
                        </Tag>
                      )}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: "#1890ff" }}>
                        {formatPriceUsd(item.price ?? 0)}
                      </div>
                      {item.priority && (
                        <Tag color="red" size="small">
                          🔥 {t("priority")}
                        </Tag>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12 }}>
                    <div>
                      <div style={{ color: "#888", marginBottom: 2 }}>{t("ordered")}</div>
                      <div style={{ fontWeight: 600 }}>
                        {item.amount}
                        {item.adjustedAmount !== undefined && item.adjustedAmount !== item.amount && (
                          <Tag color="orange" size="small" style={{ marginLeft: 4 }}>
                            → {item.adjustedAmount}
                          </Tag>
                        )}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: "#888", marginBottom: 2 }}>{t("inner_packaging_qty")}</div>
                      <div style={{ fontWeight: 600 }}>{item.innerPackagingQuantity || "-"}</div>
                    </div>
                    <div>
                      <div style={{ color: "#888", marginBottom: 2 }}>{t("master_packaging_qty")}</div>
                      <div style={{ fontWeight: 600 }}>{item.masterPackagingQuantity || "-"}</div>
                    </div>
                    <div>
                      <div style={{ color: "#888", marginBottom: 2 }}>{t("subtotal")}</div>
                      <div style={{ fontWeight: 600, color: "#1890ff" }}>
                        {formatPriceUsd((item.price ?? 0) * (item.adjustedAmount ?? item.amount))}
                      </div>
                    </div>
                  </div>
                  {hasDiscrepancy && (
                    <div
                      style={{ marginTop: 8, padding: 8, backgroundColor: "#fff7e6", borderRadius: 4, fontSize: 12 }}
                    >
                      <strong style={{ color: "#fa8c16" }}>⚠️ {t("discrepancy")}</strong>
                      <div style={{ marginTop: 4 }}>
                        {t("received")}: {receivedItem.receivedAmount} (verwacht: {item.adjustedAmount || item.amount})
                      </div>
                    </div>
                  )}
                  {receivedItem?.notes && (
                    <div
                      style={{ marginTop: 8, padding: 8, backgroundColor: "#f6ffed", borderRadius: 4, fontSize: 12 }}
                    >
                      <strong>{t("notes")}:</strong> {receivedItem.notes}
                    </div>
                  )}
                </MobileProductCard>
              );
            })}
          </Space>
        ) : (
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            {(isEditing ? editedProducts : order.products).map((item, index) => {
              const receivedItem = order.receivedProducts?.find((rp) => rp.productId === item.productId);
              const hasDiscrepancy =
                receivedItem && receivedItem.receivedAmount !== (item.adjustedAmount || item.amount);

              return (
                <Card
                  key={index}
                  size="small"
                  extra={
                    isEditing && (
                      <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleRemoveProduct(index)}>
                        {t("delete")}
                      </Button>
                    )
                  }
                >
                  <Descriptions
                    title={
                      <span>
                        <strong>{item.productName}</strong>
                        {item.productColor && (
                          <span style={{ color: "#666", marginLeft: 8 }}>({item.productColor})</span>
                        )}
                        {item.productDraftId && (
                          <Tag color="gold" style={{ marginLeft: 8 }}>
                            {t("product_draft.new_product")}
                          </Tag>
                        )}
                      </span>
                    }
                    bordered
                    column={1}
                    size="small"
                    labelStyle={{ maxWidth: 200, width: 200, color: "#000" }}
                  >
                    <StyledDescriptionsItem label={t("ordered")}>
                      {isEditing ? (
                        <Space>
                          <InputNumber
                            min={0}
                            value={item.amount}
                            onChange={(value) => handleProductChange(index, "amount", value || 0)}
                            style={{ width: 100 }}
                          />
                          {item.adjustedAmount !== undefined && item.adjustedAmount !== item.amount && (
                            <Tag color="orange">
                              {t("was")}: {item.adjustedAmount} ({t("supplier_proposal")})
                            </Tag>
                          )}
                        </Space>
                      ) : (
                        <span>
                          {item.amount}
                          {item.adjustedAmount !== undefined && item.adjustedAmount !== item.amount && (
                            <Tag color="orange" style={{ marginLeft: 8 }}>
                              → {item.adjustedAmount} ({t("changed_by_supplier")})
                            </Tag>
                          )}
                        </span>
                      )}
                      {item.priority && (
                        <Tag color="red" style={{ marginLeft: 8 }}>
                          🔥 {t("priority")}
                        </Tag>
                      )}
                    </StyledDescriptionsItem>

                    <StyledDescriptionsItem label="Code">{item.productCode || "-"}</StyledDescriptionsItem>

                    <StyledDescriptionsItem label="Barcode">{item.productBarcode || "-"}</StyledDescriptionsItem>

                    <StyledDescriptionsItem label="Afmetingen">
                      {(() => {
                        const currentDim = item.adjustedProductDimensions || item.productDimensions;
                        const originalDim = item.productDimensions;
                        const hasChanges =
                          item.adjustedProductDimensions &&
                          JSON.stringify(item.adjustedProductDimensions) !== JSON.stringify(item.productDimensions);

                        let currentText = "-";
                        let originalText = "";

                        if (currentDim) {
                          if (currentDim.length && currentDim.width && currentDim.height) {
                            currentText = `L: ${currentDim.length} × B: ${currentDim.width} × H: ${currentDim.height} cm`;
                          } else if (currentDim.height && currentDim.diameter) {
                            currentText = `H: ${currentDim.height} × Ø: ${currentDim.diameter} cm`;
                          } else if (currentDim.length && currentDim.width) {
                            currentText = `L: ${currentDim.length} × B: ${currentDim.width} cm`;
                          }
                        }

                        if (hasChanges && originalDim) {
                          if (originalDim.length && originalDim.width && originalDim.height) {
                            originalText = `L: ${originalDim.length} × B: ${originalDim.width} × H: ${originalDim.height} cm`;
                          } else if (originalDim.height && originalDim.diameter) {
                            originalText = `H: ${originalDim.height} × Ø: ${originalDim.diameter} cm`;
                          } else if (originalDim.length && originalDim.width) {
                            originalText = `L: ${originalDim.length} × B: ${originalDim.width} cm`;
                          }
                        }

                        return (
                          <span>
                            {currentText}
                            {hasChanges && originalText && (
                              <Tag color="orange" style={{ marginLeft: 8 }}>
                                → {currentText} (wijziging door leverancier, was: {originalText})
                              </Tag>
                            )}
                          </span>
                        );
                      })()}
                    </StyledDescriptionsItem>

                    <StyledDescriptionsItem label={t("inner_packaging_qty")}>
                      <span>
                        {item.innerPackagingQuantity || "-"}
                        {item.adjustedInnerPackagingQuantity !== undefined &&
                          item.adjustedInnerPackagingQuantity !== item.innerPackagingQuantity && (
                            <Tag color="orange" style={{ marginLeft: 8 }}>
                              → {item.adjustedInnerPackagingQuantity} (wijziging door leverancier)
                            </Tag>
                          )}
                      </span>
                    </StyledDescriptionsItem>

                    <StyledDescriptionsItem label={t("master_packaging_qty")}>
                      <span>
                        {item.masterPackagingQuantity || "-"}
                        {item.adjustedMasterPackagingQuantity !== undefined &&
                          item.adjustedMasterPackagingQuantity !== item.masterPackagingQuantity && (
                            <Tag color="orange" style={{ marginLeft: 8 }}>
                              → {item.adjustedMasterPackagingQuantity} (wijziging door leverancier)
                            </Tag>
                          )}
                      </span>
                    </StyledDescriptionsItem>

                    <StyledDescriptionsItem label={t("price_per_unit")}>
                      <span>
                        {formatPriceUsd(item.price ?? 0)}
                        {item.adjustedPrice !== undefined && item.adjustedPrice !== item.price && (
                          <Tag color="orange" style={{ marginLeft: 8 }}>
                            → {formatPriceUsd(item.adjustedPrice)} (wijziging door leverancier)
                          </Tag>
                        )}
                      </span>
                    </StyledDescriptionsItem>

                    {item.supplierComment && (
                      <StyledDescriptionsItem label={t("supplier_comment")}>
                        <div
                          style={{
                            padding: 8,
                            background: "#f0f0f0",
                            borderRadius: 4,
                            fontSize: 13,
                          }}
                        >
                          {item.supplierComment}
                        </div>
                      </StyledDescriptionsItem>
                    )}

                    {item.comment && (
                      <StyledDescriptionsItem label={<span>{t("comments")}</span>}>
                        <div
                          style={{
                            fontSize: 13,
                          }}
                        >
                          {item.comment}
                        </div>
                      </StyledDescriptionsItem>
                    )}

                    {item.images && item.images.length > 0 && (
                      <StyledDescriptionsItem label={<span>{t("images")}</span>}>
                        <Space size="small" wrap>
                          {item.images.map((img, idx) => (
                            <a key={idx} href={img} target="_blank" rel="noopener noreferrer">
                              <img
                                src={img}
                                alt={`Product image ${idx + 1}`}
                                style={{
                                  width: 80,
                                  height: 80,
                                  objectFit: "cover",
                                  borderRadius: 4,
                                  border: "1px solid #d9d9d9",
                                }}
                              />
                            </a>
                          ))}
                        </Space>
                      </StyledDescriptionsItem>
                    )}

                    {receivedItem && (
                      <StyledDescriptionsItem label={t("received")}>
                        {receivedItem.receivedAmount}
                        {hasDiscrepancy && (
                          <Tag color="orange" style={{ marginLeft: 8 }}>
                            {t("discrepancy")}
                          </Tag>
                        )}
                      </StyledDescriptionsItem>
                    )}

                    {receivedItem?.discrepancyReason && (
                      <StyledDescriptionsItem label={t("discrepancy_reason")}>
                        <div
                          style={{
                            padding: 8,
                            background: "#fff7e6",
                            borderRadius: 4,
                            fontSize: 13,
                            border: "1px solid #ffd591",
                          }}
                        >
                          {receivedItem.discrepancyReason}
                        </div>
                      </StyledDescriptionsItem>
                    )}
                  </Descriptions>
                </Card>
              );
            })}
          </Space>
        )}

        {order.invoice && (
          <div style={{ marginTop: 24 }}>
            <h5 style={{ marginTop: 24 }}>{t("invoice")}</h5>

            {/* Proforma Invoice Section */}
            {(order.invoice.proformaInvoiceNumber || order.invoice.proformaPdfUrl) && (
              <Card
                title={`Proforma Invoice${order.invoice.proformaInvoiceNumber ? `: ${order.invoice.proformaInvoiceNumber}` : ""}`}
                size="small"
                style={{ marginBottom: 16, backgroundColor: "#fffbe6" }}
              >
                <Descriptions bordered column={2} size="small">
                  {order.invoice.proformaInvoiceNumber && (
                    <Descriptions.Item label={t("invoice_number")} span={1}>
                      {order.invoice.proformaInvoiceNumber}
                    </Descriptions.Item>
                  )}
                  {order.invoice.proformaInvoiceDate && (
                    <Descriptions.Item label={t("invoice_date")} span={1}>
                      {dayjs.unix(order.invoice.proformaInvoiceDate).format("DD-MM-YYYY")}
                    </Descriptions.Item>
                  )}
                  {order.invoice.proformaPdfUrl && (
                    <Descriptions.Item label="Proforma PDF" span={2}>
                      <Button
                        type="link"
                        icon={<DownloadOutlined />}
                        href={order.invoice.proformaPdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Download Proforma Invoice PDF
                      </Button>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            )}

            {/* Final Invoice Section */}
            <Card title={`Final Invoice: ${order.invoice.invoiceNumber}`} size="small">
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label={t("invoice_number")} span={1}>
                  {order.invoice.invoiceNumber}
                </Descriptions.Item>
                <Descriptions.Item label={t("invoice_date")} span={1}>
                  {dayjs.unix(order.invoice.invoiceDate).format("DD-MM-YYYY")}
                </Descriptions.Item>
                <Descriptions.Item label={t("total_amount")} span={1}>
                  {formatPriceUsd(order.invoice.totalAmount)}
                </Descriptions.Item>
                <Descriptions.Item label={t("created_by")} span={1}>
                  {order.invoice.createdBy}
                </Descriptions.Item>
                {order.invoice.remarks && (
                  <Descriptions.Item label={t("invoice_remarks")} span={2}>
                    <div
                      style={{
                        padding: 8,
                        background: "#fafafa",
                        borderRadius: 4,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {order.invoice.remarks}
                    </div>
                  </Descriptions.Item>
                )}
                {order.invoice.pdfUrl && (
                  <Descriptions.Item label={t("invoice_pdf")} span={2}>
                    <Button
                      type="link"
                      icon={<DownloadOutlined />}
                      href={order.invoice.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("download_invoice_pdf")}
                    </Button>
                  </Descriptions.Item>
                )}
                {order.invoice.exactPurchaseInvoiceId && (
                  <Descriptions.Item label={t("exact_invoice_id")} span={2}>
                    <Tag color="green">✓ {t("synced_to_exact")}</Tag>
                    <span style={{ marginLeft: 8, fontSize: 12, color: "#666" }}>
                      {order.invoice.exactPurchaseInvoiceId}
                    </span>
                  </Descriptions.Item>
                )}
              </Descriptions>

              <div style={{ marginTop: 16 }}>
                <p style={{ marginBottom: 6, fontWeight: "bold" }}>{t("invoice_lines")}</p>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#fafafa", borderBottom: "2px solid #d9d9d9" }}>
                      <th style={{ padding: 12, textAlign: "left", borderBottom: "1px solid #d9d9d9" }}>
                        {t("product")}
                      </th>
                      <th style={{ padding: 12, textAlign: "right", borderBottom: "1px solid #d9d9d9" }}>
                        {t("quantity")}
                      </th>
                      <th style={{ padding: 12, textAlign: "right", borderBottom: "1px solid #d9d9d9" }}>
                        {t("unit_price")}
                      </th>
                      <th style={{ padding: 12, textAlign: "right", borderBottom: "1px solid #d9d9d9" }}>
                        {t("discount")}
                      </th>
                      <th style={{ padding: 12, textAlign: "right", borderBottom: "1px solid #d9d9d9" }}>
                        {t("total")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.invoice.lines.map((line, idx) => (
                      <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: 12 }}>
                          <strong>{line.productName}</strong>
                          {line.productColor && (
                            <span style={{ color: "#666", marginLeft: 8 }}>({line.productColor})</span>
                          )}
                        </td>
                        <td style={{ padding: 12, textAlign: "right" }}>{line.quantity}</td>
                        <td style={{ padding: 12, textAlign: "right" }}>{formatPriceUsd(line.unitPrice)}</td>
                        <td style={{ padding: 12, textAlign: "right" }}>
                          {line.discountPercentage ? `${line.discountPercentage}%` : "-"}
                        </td>
                        <td style={{ padding: 12, textAlign: "right" }}>
                          <strong>{formatPriceUsd(line.totalPrice)}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ backgroundColor: "#f0f5ff", borderTop: "1px solid #d9d9d9" }}>
                      <td colSpan="4" style={{ padding: 12, textAlign: "right" }}>
                        <strong>Subtotal (Products):</strong>
                      </td>
                      <td style={{ padding: 12, textAlign: "right" }}>
                        <strong>
                          {formatPriceUsd(order.invoice.lines.reduce((sum, line) => sum + line.totalPrice, 0))}
                        </strong>
                      </td>
                    </tr>
                    {order.invoice.additionalCosts && order.invoice.additionalCosts.length > 0 && (
                      <tr style={{ backgroundColor: "#fff7e6", borderTop: "1px solid #d9d9d9" }}>
                        <td colSpan="4" style={{ padding: 12, textAlign: "right" }}>
                          <strong>Additional Costs:</strong>
                        </td>
                        <td style={{ padding: 12, textAlign: "right" }}>
                          <strong>
                            {formatPriceUsd(order.invoice.additionalCosts.reduce((sum, cost) => sum + cost.amount, 0))}
                          </strong>
                        </td>
                      </tr>
                    )}
                    <tr style={{ backgroundColor: "#e6f7ff", borderTop: "2px solid #1890ff" }}>
                      <td colSpan="4" style={{ padding: 12, textAlign: "right" }}>
                        <strong style={{ fontSize: 16 }}>{t("invoice_total")}:</strong>
                      </td>
                      <td style={{ padding: 12, textAlign: "right" }}>
                        <strong style={{ fontSize: 18, color: "#1890ff" }}>
                          {formatPriceUsd(order.invoice.totalAmount)}
                        </strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Additional Costs Section */}
              {order.invoice.additionalCosts && order.invoice.additionalCosts.length > 0 && (
                <div style={{ marginTop: 24 }}>
                  <p style={{ marginBottom: 12, fontWeight: "bold", fontSize: 16 }}>Additional Costs</p>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#fff7e6", borderBottom: "2px solid #d9d9d9" }}>
                        <th style={{ padding: 12, textAlign: "left", borderBottom: "1px solid #d9d9d9" }}>
                          Description
                        </th>
                        <th style={{ padding: 12, textAlign: "right", borderBottom: "1px solid #d9d9d9" }}>Quantity</th>
                        <th style={{ padding: 12, textAlign: "right", borderBottom: "1px solid #d9d9d9" }}>
                          Unit Price
                        </th>
                        <th style={{ padding: 12, textAlign: "right", borderBottom: "1px solid #d9d9d9" }}>Total</th>
                        <th style={{ padding: 12, textAlign: "left", borderBottom: "1px solid #d9d9d9" }}>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.invoice.additionalCosts.map((cost, idx) => (
                        <tr key={cost.id || idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                          <td style={{ padding: 12 }}>
                            <strong>{cost.description}</strong>
                          </td>
                          <td style={{ padding: 12, textAlign: "right" }}>{cost.quantity || 1}</td>
                          <td style={{ padding: 12, textAlign: "right" }}>
                            {cost.currency === "USD"
                              ? formatPriceUsd(cost.unitPrice || cost.amount)
                              : `${cost.currency} ${(cost.unitPrice || cost.amount).toFixed(2)}`}
                          </td>
                          <td style={{ padding: 12, textAlign: "right" }}>
                            <strong>
                              {cost.currency === "USD"
                                ? formatPriceUsd(cost.amount)
                                : `${cost.currency} ${cost.amount.toFixed(2)}`}
                            </strong>
                          </td>
                          <td style={{ padding: 12, color: "#666", fontSize: 13 }}>{cost.remarks || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ backgroundColor: "#fff7e6", borderTop: "2px solid #d9d9d9" }}>
                        <td colSpan="3" style={{ padding: 12, textAlign: "right" }}>
                          <strong>Total Additional Costs:</strong>
                        </td>
                        <td style={{ padding: 12, textAlign: "right" }}>
                          <strong style={{ fontSize: 16, color: "#fa8c16" }}>
                            {formatPriceUsd(order.invoice.additionalCosts.reduce((sum, cost) => sum + cost.amount, 0))}
                          </strong>
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </Card>
          </div>
        )}

        {order.log && order.log.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <Collapse defaultActiveKeys={["1"]}>
              <Panel header={t("activity_log")} key="1">
                <div style={{ maxHeight: 400, overflowY: "auto", paddingTop: 20 }}>
                  <Timeline>
                    {[...order.log].reverse().map((entry, idx) => (
                      <Timeline.Item key={idx}>
                        <div>
                          <p style={{ margin: 0 }}>
                            <strong>{t(`log.${entry.action}`)}</strong>
                          </p>
                          <p style={{ margin: 0, fontSize: 12, color: "#888" }}>
                            {entry.user} - {dayjs.unix(entry.timestamp).format("DD-MM-YYYY HH:mm")}
                          </p>
                          {entry.oldValue && entry.newValue && (
                            <p style={{ margin: 0, fontSize: 12 }}>
                              {t(`order.${entry.oldValue}`)} → {t(`order.${entry.newValue}`)}
                            </p>
                          )}
                          {entry.metadata && (
                            <p style={{ margin: 0, fontSize: 12, color: "#666" }}>{JSON.stringify(entry.metadata)}</p>
                          )}
                        </div>
                      </Timeline.Item>
                    ))}
                  </Timeline>
                </div>
              </Panel>
            </Collapse>
          </div>
        )}
      </Card>

      <Modal
        title={t("review_received_items")}
        open={showReviewModal}
        onCancel={() => setShowReviewModal(false)}
        onOk={handleSubmitReview}
        okText={t("submit_review")}
        cancelText={t("cancel")}
        confirmLoading={saving}
        width={isMobile ? "100%" : 800}
        styles={{ body: { padding: isMobile ? 12 : 24 } }}
      >
        <Form form={reviewForm} layout="vertical">
          <p style={{ marginBottom: 16, color: "#666" }}>{t("review_instructions")}</p>
          <Form.List name="products">
            {(fields) => (
              <>
                {fields.map((field) => {
                  const product = reviewForm.getFieldValue(["products", field.name]);
                  return (
                    <Card key={field.key} size="small" style={{ marginBottom: 16 }}>
                      <div style={{ marginBottom: 8 }}>
                        <strong>{product?.productName}</strong>
                        {product?.productColor && <span style={{ color: "#666" }}> ({product.productColor})</span>}
                      </div>

                      <Space direction="vertical" style={{ width: "100%" }}>
                        <div>
                          <span style={{ color: "#666" }}>{t("expected_amount")}:</span>{" "}
                          <strong>{product?.expectedAmount}</strong>
                        </div>
                        <Form.Item
                          {...field}
                          name={[field.name, "receivedAmount"]}
                          label={t("actually_received")}
                          rules={[
                            { required: true, message: t("please_enter_received_amount") },
                            { type: "number", min: 0, message: t("must_be_positive") },
                          ]}
                        >
                          <InputNumber style={{ width: "100%" }} min={0} />
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate>
                          {() => {
                            const receivedAmount = reviewForm.getFieldValue(["products", field.name, "receivedAmount"]);
                            const expectedAmount = product?.expectedAmount;
                            const hasDiscrepancy = receivedAmount !== expectedAmount;

                            return hasDiscrepancy ? (
                              <Form.Item
                                {...field}
                                name={[field.name, "discrepancyReason"]}
                                label={t("discrepancy_reason")}
                                rules={[{ required: true, message: t("please_enter_reason") }]}
                              >
                                <TextArea rows={2} placeholder={t("explain_why_amounts_differ")} />
                              </Form.Item>
                            ) : null;
                          }}
                        </Form.Item>
                        <Form.Item {...field} name={[field.name, "productId"]} hidden>
                          <Input />
                        </Form.Item>
                        <Form.Item {...field} name={[field.name, "productName"]} hidden>
                          <Input />
                        </Form.Item>
                        <Form.Item {...field} name={[field.name, "productColor"]} hidden>
                          <Input />
                        </Form.Item>{" "}
                        <Form.Item {...field} name={[field.name, "productCode"]} hidden>
                          <Input />
                        </Form.Item>
                        <Form.Item {...field} name={[field.name, "expectedAmount"]} hidden>
                          <InputNumber />
                        </Form.Item>
                      </Space>
                    </Card>
                  );
                })}
              </>
            )}
          </Form.List>
        </Form>
      </Modal>

      {/* Split Order Modal */}
      <Modal
        title={t("add_products_split_order")}
        open={showSplitOrderModal}
        onCancel={() => {
          setShowSplitOrderModal(false);
          setSplitOrderProducts([]);
          splitOrderForm.resetFields();
        }}
        onOk={handleCreateSplitOrder}
        confirmLoading={saving}
        okText={t("create_split_order")}
        cancelText={t("cancel")}
        width={isMobile ? "100%" : 1000}
        styles={{ body: { padding: isMobile ? 12 : 24 } }}
      >
        <div style={{ marginBottom: 16, padding: 12, backgroundColor: "#e6f7ff", borderRadius: 4 }}>
          <p style={{ margin: 0, marginBottom: 8 }}>
            <strong>ℹ️ {t("split_order_notice")}:</strong> {t("split_order_notice_text")}
          </p>
          <p style={{ margin: 0, fontSize: 13, color: "#666" }}>
            {t("new_order_number")}:{" "}
            <strong>
              {order?.orderNumber}-{String.fromCharCode(65 + (order?.childOrders?.length || 0))}
            </strong>
          </p>
        </div>

        <Form form={splitOrderForm} layout="vertical">
          <Form.Item
            label={t("reason_for_adding")}
            name="notes"
            rules={[{ required: true, message: t("provide_reason") }]}
          >
            <TextArea rows={2} placeholder={t("reason_placeholder")} />
          </Form.Item>

          <div style={{ marginBottom: 16 }}>
            <strong>{t("add_products")}:</strong>
            <Select
              showSearch
              style={{ width: "100%", marginTop: 8 }}
              placeholder={t("search_select_product")}
              optionFilterProp="children"
              onChange={handleAddProductToSplitOrder}
              value={null}
              filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
              options={(() => {
                const supplier = suppliers?.find((s) => s.id === order.supplierId);
                const hasSupplierProducts = supplier?.productIds && supplier.productIds.length > 0;
                const supplierProducts = hasSupplierProducts
                  ? products?.filter((p) => supplier.productIds.includes(p.id))
                  : [];

                return supplierProducts
                  ?.filter((p) => !splitOrderProducts.some((sp) => sp.productId === p.id))
                  .map((p) => ({
                    value: p.id,
                    label: `${p.name}${p.color ? ` (${p.color})` : ""} - ${p.code || ""}`,
                  }));
              })()}
            />
          </div>

          {splitOrderProducts.length > 0 && (
            <div>
              <strong>{t("added_products")}:</strong>
              <Space direction="vertical" style={{ width: "100%", marginTop: 8 }} size="small">
                {splitOrderProducts.map((product, index) => (
                  <Card key={index} size="small">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: 8 }}>
                          <strong>{product.productName}</strong>
                          {product.productColor && <span style={{ color: "#666" }}> ({product.productColor})</span>}
                        </div>
                        <Space>
                          <div>
                            <div style={{ fontSize: 12, color: "#666" }}>{t("quantity")}:</div>
                            <InputNumber
                              min={1}
                              value={product.amount}
                              onChange={(value) => handleSplitOrderProductChange(index, "amount", value || 0)}
                              style={{ width: 100 }}
                            />
                          </div>
                          <div>
                            <div style={{ fontSize: 12, color: "#666" }}>{t("price_per_piece")}:</div>
                            <InputNumber
                              min={0}
                              step={0.01}
                              prefix="$"
                              value={product.price}
                              onChange={(value) => handleSplitOrderProductChange(index, "price", value || 0)}
                              style={{ width: 120 }}
                            />
                          </div>
                          {product.cbm > 0 && (
                            <div>
                              <div style={{ fontSize: 12, color: "#666" }}>{t("cbm_per_piece")}:</div>
                              <div style={{ padding: "4px 11px", border: "1px solid #d9d9d9", borderRadius: 2 }}>
                                {product.cbm} m³
                              </div>
                            </div>
                          )}
                        </Space>
                        <div style={{ marginTop: 8, fontSize: 13, color: "#666" }}>
                          {t("total")}: <strong>{formatPriceUsd(product.amount * product.price)}</strong>
                          {product.cbm > 0 && ` | ${(product.amount * product.cbm).toFixed(2)} m³`}
                        </div>
                      </div>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveProductFromSplitOrder(index)}
                      />
                    </div>
                  </Card>
                ))}
              </Space>

              <div style={{ marginTop: 16, padding: 12, backgroundColor: "#f6ffed", borderRadius: 4 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span>{t("total_products_count")}:</span>
                  <strong>{splitOrderProducts.length}</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span>{t("total_amount")}:</span>
                  <strong style={{ fontSize: 16, color: "#52c41a" }}>
                    {formatPriceUsd(calculateSplitOrderTotals().totalAmount)}
                  </strong>
                </div>
                {calculateSplitOrderTotals().totalCbm > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>{t("total_cbm")}:</span>
                    <strong>{calculateSplitOrderTotals().totalCbm.toFixed(2)} m³</strong>
                  </div>
                )}
              </div>
            </div>
          )}

          {splitOrderProducts.length === 0 && (
            <div style={{ padding: 24, textAlign: "center", color: "#999" }}>{t("no_products_added")}</div>
          )}
        </Form>
      </Modal>

      {/* Payment Modal */}
      <Modal
        title={order?.payments && order.payments.length > 0 ? t("add_payment_title") : t("mark_as_paid_title")}
        open={showPaymentModal}
        onCancel={() => {
          setShowPaymentModal(false);
          paymentForm.resetFields();
          setPaymentProofFile(null);
        }}
        onOk={handleMarkAsPaid}
        confirmLoading={uploadingPayment}
        okText={t("save")}
        cancelText={t("cancel")}
        width={isMobile ? "100%" : 600}
        styles={{ body: { padding: isMobile ? 12 : 24 } }}
      >
        {order?.payments && order.payments.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <strong>{t("previous_payments")}:</strong>
            <div style={{ marginTop: 8 }}>
              {order.payments.map((payment, index) => (
                <div key={index} style={{ padding: 8, backgroundColor: "#f6ffed", borderRadius: 4, marginBottom: 4 }}>
                  <strong>
                    {order.currency === "IDR"
                      ? `IDR ${payment.amount.toLocaleString("id-ID")}`
                      : formatPriceUsd(payment.amount)}
                  </strong>
                  {payment.exchangeRate && (
                    <span style={{ marginLeft: 8, fontSize: 12, color: "#1890ff" }}>
                      (€{" "}
                      {(payment.amount * payment.exchangeRate).toLocaleString("nl-NL", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      )
                    </span>
                  )}
                  <span style={{ marginLeft: 8, fontSize: 12, color: "#666" }}>
                    op {dayjs.unix(payment.paidAt).format("DD-MM-YYYY")}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <Form
          form={paymentForm}
          layout="vertical"
          initialValues={{
            paidAmount:
              order?.invoice?.totalAmount || order?.totalPrice
                ? (order.invoice?.totalAmount || order.totalPrice) - (order.totalPaid || 0)
                : 0,
          }}
        >
          <Form.Item
            label={`${t("paid_amount")} (${order?.currency || "USD"})`}
            name="paidAmount"
            rules={[
              { required: true, message: t("enter_paid_amount") },
              { type: "number", min: 0, message: t("amount_must_be_positive") },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              prefix={order?.currency === "IDR" ? "IDR" : "$"}
              precision={order?.currency === "IDR" ? 0 : 2}
              step={order?.currency === "IDR" ? 100000 : 100}
              placeholder={order?.currency === "IDR" ? `${t("example")} 50000000` : `${t("example")} 5000.00`}
              formatter={
                order?.currency === "IDR" ? (value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".") : undefined
              }
              parser={order?.currency === "IDR" ? (value) => value.replace(/\./g, "") : undefined}
            />
          </Form.Item>

          <Form.Item
            label={`${t("exchange_rate")} ${order?.currency || "USD"} → EUR`}
            name="exchangeRate"
            tooltip={t("exchange_rate_tooltip")}
            rules={[{ required: true, message: t("form.enter_value") }]}
          >
            <InputNumber
              min={0}
              step={0.01}
              precision={order?.currency === "IDR" ? 6 : 4}
              placeholder={order?.currency === "IDR" ? "0.000057" : "0.8600"}
              style={{ width: "100%" }}
              addonBefore={`1 ${order?.currency || "USD"} =`}
              addonAfter="EUR"
            />
          </Form.Item>

          <div style={{ marginBottom: 16, padding: 12, backgroundColor: "#f0f5ff", borderRadius: 4 }}>
            <div style={{ marginBottom: 4 }}>
              <strong>{order?.invoice ? `${t("invoice_total")}:` : `${t("order_total")}:`}</strong>{" "}
              {order?.currency === "IDR"
                ? `IDR ${(order?.invoice?.totalAmount || order?.totalPrice || 0).toLocaleString("id-ID")}`
                : formatPriceUsd(order?.invoice?.totalAmount || order?.totalPrice || 0)}
            </div>
            {order?.totalPaid > 0 && (
              <div style={{ marginBottom: 4 }}>
                <strong>{t("already_paid")}:</strong>{" "}
                {order?.currency === "IDR"
                  ? `IDR ${order.totalPaid.toLocaleString("id-ID")}`
                  : formatPriceUsd(order.totalPaid)}
              </div>
            )}
            <div style={{ marginBottom: 8 }}>
              <strong>{t("still_to_pay")}:</strong>{" "}
              <span style={{ color: "#ff4d4f", fontWeight: "bold" }}>
                {order?.currency === "IDR"
                  ? `IDR ${((order?.invoice?.totalAmount || order?.totalPrice || 0) - (order?.totalPaid || 0)).toLocaleString("id-ID")}`
                  : formatPriceUsd((order?.invoice?.totalAmount || order?.totalPrice || 0) - (order?.totalPaid || 0))}
              </span>
            </div>
            <div style={{ fontSize: 12, color: "#666" }}>
              {order?.payments && order.payments.length > 0 ? t("add_next_payment") : t("deposit_info")}
            </div>
          </div>

          <Form.Item label={t("payment_proof")} help={t("payment_proof_help")}>
            <Upload
              beforeUpload={(file) => {
                setPaymentProofFile(file);
                return false; // Prevent automatic upload
              }}
              onRemove={() => setPaymentProofFile(null)}
              maxCount={1}
              accept="image/*,.pdf"
            >
              <Button icon={<UploadOutlined />}>{t("select_file")}</Button>
            </Upload>
          </Form.Item>

          <Form.Item label={t("payment_notes")} name="paymentNotes">
            <TextArea rows={3} placeholder={t("payment_notes_placeholder_full")} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const MobileProductCard = styled(Card)`
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  margin-bottom: 4px;

  .ant-card-body {
    padding: 12px;
  }
`;

const StyledDescriptionsItem = styled(Descriptions.Item)`
  .ant-descriptions-item-label {
    font-weight: 500;
  }
`;

export default PurchaseOrderDetail;
